import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NGOVerification = ({ verificationData, onScheduleInspection }) => {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'scheduled':
        return 'var(--color-warning)';
      case 'pending':
        return 'var(--color-muted-foreground)';
      case 'failed':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'scheduled':
        return 'Clock';
      case 'pending':
        return 'Calendar';
      case 'failed':
        return 'XCircle';
      default:
        return 'HelpCircle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completada';
      case 'scheduled':
        return 'Programada';
      case 'pending':
        return 'Pendiente';
      case 'failed':
        return 'No Aprobada';
      default:
        return 'Sin Estado';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Verificación NGO
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Inspecciones presenciales y resultados
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={20} color="var(--color-primary)" />
          <span className="text-sm font-medium text-foreground">
            {verificationData?.ngoPartner}
          </span>
        </div>
      </div>
      {/* Current Status */}
      <div className="bg-muted/30 p-4 rounded-lg mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-foreground">Estado Actual</h3>
          <div className="flex items-center space-x-2">
            <Icon 
              name={getStatusIcon(verificationData?.currentStatus)} 
              size={16} 
              color={getStatusColor(verificationData?.currentStatus)} 
            />
            <span 
              className="text-sm font-medium"
              style={{ color: getStatusColor(verificationData?.currentStatus) }}
            >
              {getStatusLabel(verificationData?.currentStatus)}
            </span>
          </div>
        </div>

        {verificationData?.nextInspection && (
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>Próxima inspección: {formatDate(verificationData?.nextInspection)}</span>
          </div>
        )}
      </div>
      {/* Inspection History */}
      <div className="mb-6">
        <h3 className="font-medium text-foreground mb-4">Historial de Inspecciones</h3>
        <div className="space-y-3">
          {verificationData?.inspectionHistory?.map((inspection) => (
            <div key={inspection?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={getStatusIcon(inspection?.status)} 
                    size={20} 
                    color={getStatusColor(inspection?.status)} 
                  />
                  <div>
                    <h4 className="font-medium text-foreground">
                      Inspección #{inspection?.id}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(inspection?.date)} - {inspection?.inspector}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-foreground">
                    {inspection?.score}/100
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Puntuación
                  </div>
                </div>
              </div>

              {inspection?.notes && (
                <div className="bg-muted/50 p-3 rounded-md">
                  <h5 className="text-sm font-medium text-foreground mb-1">
                    Notas del Inspector
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    {inspection?.notes}
                  </p>
                </div>
              )}

              {inspection?.recommendations && inspection?.recommendations?.length > 0 && (
                <div className="mt-3">
                  <h5 className="text-sm font-medium text-foreground mb-2">
                    Recomendaciones
                  </h5>
                  <ul className="space-y-1">
                    {inspection?.recommendations?.map((rec, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <Icon name="ArrowRight" size={14} color="var(--color-muted-foreground)" className="mt-0.5" />
                        <span className="text-muted-foreground">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Schedule New Inspection */}
      {verificationData?.canSchedule && (
        <div className="border-t border-border pt-6">
          <h3 className="font-medium text-foreground mb-4">Programar Nueva Inspección</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {verificationData?.availableSlots?.map((slot) => (
              <button
                key={slot?.id}
                onClick={() => setSelectedTimeSlot(slot?.id)}
                className={`p-3 border rounded-lg text-left transition-smooth ${
                  selectedTimeSlot === slot?.id
                    ? 'border-primary bg-primary/10' :'border-border hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground">{formatDate(slot?.datetime)}</p>
                    <p className="text-sm text-muted-foreground">{slot?.inspector}</p>
                  </div>
                  <Icon 
                    name={selectedTimeSlot === slot?.id ? 'CheckCircle' : 'Circle'} 
                    size={20} 
                    color={selectedTimeSlot === slot?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} 
                  />
                </div>
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="default" 
              className="flex-1"
              disabled={!selectedTimeSlot}
              onClick={() => onScheduleInspection(selectedTimeSlot)}
            >
              <Icon name="Calendar" size={16} className="mr-2" />
              Confirmar Cita
            </Button>
            <Button variant="outline" className="flex-1">
              <Icon name="MessageCircle" size={16} className="mr-2" />
              Contactar NGO
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NGOVerification;